import type { FurniturePart } from "../../types";
import { blob, circle, clamp, ellipse, part, rect, resetPartIds, seam } from "../geometry";
import { bool, str, type BuildContext, type BuildResult, type FurnitureTemplate } from "./types";

export const rugTemplate: FurnitureTemplate = {
  id: "rug",
  label: "Rug",
  frontKind: "none",
  defaultMaterial: "wool-rug",
  params: [
    {
      id: "shape",
      label: "Shape",
      type: "enum",
      default: "rect",
      options: [
        { value: "rect", label: "Rectangle" },
        { value: "round", label: "Round" },
      ],
    },
    { id: "border", label: "Border", type: "bool", default: true },
  ],
  build({ w, d, params }: BuildContext): BuildResult {
    resetPartIds();
    const shape = str(params, "shape", "rect");
    const border = bool(params, "border", true);
    const parts: FurniturePart[] = [];
    if (shape === "round") {
      const fp = ellipse(0, 0, w / 2, d / 2);
      parts.push(part("body", fp, 0, 0.5));
      if (border) {
        parts.push(part("detail", ellipse(0, 0, w / 2 - 3, d / 2 - 3), 0.5, 0.55, { translucent: true }));
      }
      return { parts, footprint: fp };
    }
    const body = part("body", rect(-w / 2, -d / 2, w, d), 0, 0.5, { cornerRadius: 0.5 });
    parts.push(body);
    if (border) {
      const inset = clamp(Math.min(w, d) * 0.06, 2, 5);
      const x0 = -w / 2 + inset;
      const x1 = w / 2 - inset;
      const y0 = -d / 2 + inset;
      const y1 = d / 2 - inset;
      // Inner field slightly lighter than the border band, plus a woven
      // border line so the rug reads as a rug and not a floor tile.
      parts.push(part("cushion", rect(x0, y0, x1 - x0, y1 - y0), 0.5, 0.55, {
        cornerRadius: 0.25,
        seams: [seam(x0 + 1, y0 + 1, x1 - 1, y0 + 1), seam(x1 - 1, y0 + 1, x1 - 1, y1 - 1), seam(x1 - 1, y1 - 1, x0 + 1, y1 - 1), seam(x0 + 1, y1 - 1, x0 + 1, y0 + 1)],
      }));
    }
    return { parts };
  },
};

export const lampTemplate: FurnitureTemplate = {
  id: "lamp",
  label: "Floor lamp",
  frontKind: "none",
  defaultMaterial: "matte",
  params: [
    {
      id: "shade",
      label: "Shade",
      type: "enum",
      default: "drum",
      options: [
        { value: "drum", label: "Drum" },
        { value: "arc", label: "Arc" },
      ],
    },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const shade = str(params, "shade", "drum");
    const r = Math.min(w, d) / 2;
    const parts: FurniturePart[] = [];
    const fp = ellipse(0, 0, w / 2, d / 2);
    if (shade === "arc") {
      // Base off to the back-left, shade over the front-right.
      parts.push(part("base", circle(-r * 0.45, -r * 0.45, r * 0.4), 0, 1.5, { material: { id: "metal", tint: "#3a3a44" } }));
      parts.push(part("detail", circle(-r * 0.45, -r * 0.45, r * 0.06), 1.5, h, { material: { id: "metal", tint: "#3a3a44" } }));
      parts.push(part("shade", circle(r * 0.35, r * 0.35, r * 0.6), h - 8, h, { translucent: true }));
      return { parts, footprint: fp };
    }
    parts.push(part("base", circle(0, 0, r * 0.45), 0, 1.5, { material: { id: "metal", tint: "#3a3a44" } }));
    parts.push(part("detail", circle(0, 0, r * 0.08), 1.5, h - 10, { material: { id: "metal", tint: "#3a3a44" } }));
    parts.push(part("shade", fp, h - 10, h, { translucent: true }));
    return { parts, footprint: fp };
  },
};

export const plantTemplate: FurnitureTemplate = {
  id: "plant",
  label: "Plant",
  frontKind: "none",
  defaultMaterial: "foliage",
  params: [
    {
      id: "kind",
      label: "Kind",
      type: "enum",
      default: "leafy",
      options: [
        { value: "leafy", label: "Leafy" },
        { value: "tree", label: "Tree" },
        { value: "succulent", label: "Succulent" },
      ],
    },
  ],
  build({ w, d, params }: BuildContext): BuildResult {
    resetPartIds();
    const kind = str(params, "kind", "leafy");
    const r = Math.min(w, d) / 2;
    const parts: FurniturePart[] = [];
    const pot = { id: "ceramic" as const, tint: "#b8704f" };
    if (kind === "tree") {
      parts.push(part("pot", circle(0, 0, r * 0.42), 0, 14, { material: pot }));
      parts.push(part("foliage", blob(0, 0, r * 0.98, 3, 56), 30, 72));
      parts.push(part("foliage", blob(-r * 0.15, -r * 0.1, r * 0.55, 7, 40), 40, 76));
      return { parts, footprint: circle(0, 0, r) };
    }
    if (kind === "succulent") {
      parts.push(part("pot", circle(0, 0, r), 0, 10, { material: pot }));
      parts.push(part("foliage", blob(0, 0, r * 0.8, 11, 40), 10, 16));
      parts.push(part("foliage", blob(0, 0, r * 0.45, 5, 28), 14, 20));
      return { parts, footprint: circle(0, 0, r) };
    }
    parts.push(part("pot", circle(0, 0, r * 0.55), 0, 14, { material: pot }));
    parts.push(part("foliage", blob(0, 0, r * 0.96, 2, 52), 14, 50));
    parts.push(part("foliage", blob(r * 0.12, r * 0.08, r * 0.5, 9, 36), 30, 56));
    return { parts, footprint: circle(0, 0, r) };
  },
};

export const boxTemplate: FurnitureTemplate = {
  id: "box",
  label: "Generic",
  frontKind: "none",
  defaultMaterial: "matte",
  params: [{ id: "frontLine", label: "Mark front edge", type: "bool", default: false }],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const frontLine = bool(params, "frontLine", false);
    const body = part("body", rect(-w / 2, -d / 2, w, d), 0, h, {
      cornerRadius: clamp(Math.min(w, d) * 0.06, 0.5, 3),
    });
    if (frontLine) {
      body.seams = [seam(-w / 2 + 2, d / 2 - 1.5, w / 2 - 2, d / 2 - 1.5)];
    }
    return { parts: [body] };
  },
};
